import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_TYPE,
} from '@discocil/fv-components';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  combineLatest,
  defer,
  finalize,
  first,
  forkJoin,
  fromEventPattern,
  map,
  Observable,
  of,
  race,
  switchMap,
  tap,
  timer
} from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth/auth.service';
import { DEFAULT_CURRENCY, NOT_FOUND_LABEL } from 'src/app/config/app.config';
import { Account } from 'src/app/core/entities/account/domain/model/account.model';
import { AccountStorePort } from 'src/app/core/entities/account/domain/ports/account-store.port';
import { ACCOUNT_STORE } from 'src/app/core/entities/account/domain/tokens/account-store.token';
import { AccountApiMapper } from 'src/app/core/entities/account/infrastructure/mappers/account-api.mapper';
import { DeviceService } from 'src/app/core/entities/device/device.service';
import { Device } from 'src/app/core/entities/device/models/device.model';
import { EntryMapper } from 'src/app/core/entities/entry/infrastructure/mapper/entry.mapper';
import {
  IApiPayment,
  IApiPaymentIntegratedPayload,
  IApiPaymentTransactionResponse,
} from 'src/app/core/entities/payment/domain/interfaces/payment-api.interface';
import { Payment } from 'src/app/core/entities/payment/domain/model/payment.model';
import { TicketEvent } from 'src/app/core/entities/ticket-events/interface/ticket-event.model';
import { Ticket } from 'src/app/core/entities/ticket/domain/model/ticket.model';
import { TicketMapper } from 'src/app/core/entities/ticket/infrastructure/mapper/ticket.mapper';
import { CONSUMPTION_TYPE } from 'src/app/core/enums/consumption-type.enum';
import { INVOICING_STATUS } from 'src/app/core/enums/invoicing-status.enum';
import { PAYMENT_FLOW } from 'src/app/core/enums/payment-flow.enum';
import { PAYMENT_PROVIDER } from 'src/app/core/enums/payment-provider.enum';
import { PAYMENT_STATUS } from 'src/app/core/enums/payment-status.enum';
import { IAudit } from 'src/app/core/interfaces/audit.interface';
import { Logger } from 'src/app/core/interfaces/Logger';
import { IOrganization } from 'src/app/core/entities/organization/domain/interfaces/organization.interface';
import {
  IPaymentDetails,
  IPaymentIndexedDBData,
} from 'src/app/core/interfaces/payment.interface';
import { SplashartLoaderComponent } from 'src/app/shared/components/splashart-loader/splashart-loader.component';
import { PAYMENT_TYPE } from 'src/app/shared/enums/payment-type.enum';
import { FeatureFlagAdapter } from 'src/app/shared/infrastructure/adapters/feature-flag.adapter';
import { GlobalConfirmService } from 'src/app/shared/services/global-confirm/global-confirm.service';
import { GlobalSplashArtLoaderService } from 'src/app/shared/services/global-splashart-loader/global-splashart-loader.service';
import * as uuid from 'uuid';
import { ProcessManualPaymentUseCase } from '../../../entities/payment/application/use-cases/process-manual-payment.use-case';
import { PaymentMapper } from '../../../entities/payment/domain/mappers/payment.mapper';
import { TerminalManagerService } from '../../terminal-manager/terminal-manager.service';
import { TrackingService } from '../../tracking/tracking.service';
import { createPaymentTracker } from '../../tracking/utils/payment-tracker.util';
import { IPaymentInfo } from '../interfaces/payment-info.interface';
import { IPaymentProcessor } from '../interfaces/payment-processor.interface';
import { PaymentsCommonService } from '../payments-common.service';
import {
  formatPaymentOperation,
  hasPaymentChanged,
} from '../utils/payments-processors.utils';
import { EntriesChangedEvent } from "../../../../shared/application/events/entry/entries-changed.event";
import { EVENT_BUS } from "../../../../shared/domain/tokens/event-bus.token";
import { CAPACITOR_SERVICE } from '../../../../shared/capacitor-service/token/capacitor-service.token';

import { Drawer } from '@features/service/domain/models/drawer.model';
import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Worker } from '@features/service/domain/models/worker.model';
import { WorkerMapper } from '@features/service/infrastructure/mappers/worker.mapper';

/**
 * Service responsible for handling integrated payment workflows.
 * Manages the complete process for payments through terminal devices or Tap2Pay,
 * including payment creation, communication with payment hardware, and handling
 * success/error scenarios.
 */
@Injectable({
  providedIn: 'root',
})
export class IntegratedPaymentService implements IPaymentProcessor {
  private readonly paymentsCommonService = inject(PaymentsCommonService);
  private readonly trackingService = inject(TrackingService);
  private readonly deviceService = inject(DeviceService);
  private readonly logger = inject(Logger);
  private readonly terminalService = inject(TerminalManagerService);
  private readonly authService = inject(AuthService);
  private readonly capacitorService = inject(CAPACITOR_SERVICE);
  private readonly globalSplashArtLoaderService = inject(
    GlobalSplashArtLoaderService
  );
  private readonly processManualPaymentUseCase = inject(
    ProcessManualPaymentUseCase
  );
  private readonly globalConfirmService = inject(GlobalConfirmService);
  private readonly translateService = inject(TranslateService);
  private readonly featureFlagService = inject(FeatureFlagAdapter);
  private readonly destroyRef = inject(DestroyRef);
  private readonly trackPayment = createPaymentTracker(this.trackingService);
  private readonly accountStoreService = inject<AccountStorePort>(ACCOUNT_STORE);
  private readonly eventBus = inject(EVENT_BUS);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);
  private readonly workerMapper = inject(WorkerMapper);

  payment: Payment = new Payment();
  currentAccount!: Account | null;
  device: Device | null = null;
  organization: IOrganization | null = null;
  currentTicketEvent: TicketEvent | null = null;
  currentDrawer: Drawer | null = null;
  paymentDetails!: IPaymentDetails;
  currentWorker: Worker | null = null;
  currentAudit!: IAudit | null;
  token: string | null = '';
  ticket!: Ticket | null;

  constructor() {}

  /**
   * Processes an integrated payment transaction.
   * Handles the entire payment flow, including payment creation, ticket generation,
   * and communication with payment terminals or Tap2Pay through Capacitor.
   *
   * @param paymentDetails - The payment details containing information about the transaction
   * @returns Observable with the payment transaction response
   */
  processPayment(
    paymentDetails: IPaymentDetails
  ): Observable<IApiPaymentTransactionResponse> {
    this.paymentDetails = {...paymentDetails};

    return forkJoin({
      token: this.authService.getAuthToken(),
      necessaryData: this.getPaymentNecessaryData(),
    }).pipe(
      first(),
      switchMap(({token}) => {
        this.token = token;

        this.trackPayment(
          `Integrated payment initiated. Method: ${paymentDetails.paymentSummary.paymentMethod}, type: ${paymentDetails.paymentSummary.paymentType}`
        );

        this.trackPayment(
          `isTicketGenerated: ${paymentDetails.isTicketGenerated}`
        );
        this.trackPayment(`isClosedAccount: ${paymentDetails.isAccountClosed}`);
        this.trackPayment(
          `Amount to pay: ${paymentDetails.paymentSummary.totalAmount}`
        );

        this.trackPayment(
          `Entries to pay:`, {
            customData: {
              entries: paymentDetails.entriesToPay.map((entry) => {
                const {
                  _id,
                  product_name,
                  unitPrice,
                  total,
                  quantity,
                  selectedAddons,
                } = entry;

                return {
                  id: _id,
                  quantity: quantity,
                  name: product_name,
                  unit: unitPrice,
                  total: total,
                  selected_addons: selectedAddons,
                };
              }),
            },
          }
        );

        if (this.paymentDetails.isTicketGenerated) {
          this.paymentDetails = {
            ...this.paymentDetails,
            ticketSerie: INVOICING_STATUS.pending,
          };
        }

        const paymentInfo: IPaymentInfo = {
          currentDrawer: this.currentDrawer,
          currentWorker: this.currentWorker,
          organization: this.organization,
          paymentFlow: PAYMENT_FLOW.integrated,
          ticketSerie: this.paymentDetails.ticketSerie,
          paymentDetails,
        };

        const failedPayment = this.currentAccount?.getPaymentByStatus(
          PAYMENT_STATUS.failed
        );

        if (failedPayment) {
          this.trackPayment('Has failed payment', {
            customData: {
              paymentId: failedPayment._id,
            },
          });

          this.onPaymentFailed(failedPayment, paymentInfo);
        } else {
          this.payment = this.createPayment();

          this.payment.updateOperation(formatPaymentOperation(this.payment));

          const hasComplementEntries = this.currentAccount?.entries?.some(
            (entry) => entry?.consumption?.type === CONSUMPTION_TYPE.complement
          );

          if (hasComplementEntries) {
            this.payment.updateAdditionalData({
              ticket_event_id: this.currentTicketEvent?._id,
            });
          }

          this.currentAccount?.addPayment(this.payment);

          this.trackPayment('New payment created', {
            customData: {
              paymentId: this.payment._id,
            },
          });
        }

        const insertAccount$ = combineLatest({
          selectedAccount: this.accountStoreService.saveSelectedAccount(
            this.currentAccount as Account
          ),
          allAccounts: this.accountStoreService.saveAccounts([
            this.currentAccount as Account,
          ]),
        });

        const insertTicket$ = this.paymentsCommonService.handleTicketGeneration(
          paymentDetails,
          this.currentAccount,
          this.payment
        )
          .pipe(
            first(),
            switchMap((ticket) => {
              this.ticket = ticket;

              const payload = this.generateIntegratedPayload();
              const isTerminalPayment = !!payload.poiid;

              this.trackIntegratedType(isTerminalPayment);

              return this.startIntegratedPaymentFlow(
                isTerminalPayment,
                payload
              );
            })
          );

        return insertAccount$.pipe(
          first(),
          switchMap(() => insertTicket$)
        );
      })
    );
  }

  /**
   * Initiates the integrated payment flow by communicating with Capacitor
   * and handling the payment events.
   *
   * @param isTerminalPayment - Whether the payment is through a physical terminal
   * @param payload - The payment payload to send to the native layer
   * @returns Observable with the payment transaction response
   */
  private startIntegratedPaymentFlow(
    isTerminalPayment: boolean,
    payload: IApiPaymentIntegratedPayload
  ): Observable<IApiPaymentTransactionResponse> {
    return defer(() => {
      const paymentEvents$ =
        this.handlePostIntegratedPayment(isTerminalPayment);

      this.capacitorService.postMessage('startAdyenPayment', payload);

      this.trackPayment('Starting integrating payment in capacitor', {
        customData: {
          payload,
        },
      });

      return paymentEvents$;
    });
  }

  /**
   * Handles the payment response from the integrated payment flow.
   * Sets up listeners for success and error events, and manages the loading UI.
   *
   * @param isTerminalPayment - Whether the payment is through a physical terminal
   * @returns Observable with the payment transaction response
   */
  private handlePostIntegratedPayment(
    isTerminalPayment: boolean
  ): Observable<IApiPaymentTransactionResponse> {
    const paymentResponse$ = fromEventPattern<IApiPaymentTransactionResponse>(
      (handler) =>
        this.capacitorService.addListener('responseAdyenPayment', handler)
    ).pipe(first());

    const paymentResponseError$ =
      fromEventPattern<IApiPaymentTransactionResponse>((handler) =>
        this.capacitorService.addListener('responseAdyenPaymentError', handler)
      ).pipe(first());

    // TODO Make something to activate mock payments DONT REMOVE. THIS IS USED TO TEST THE PAYMENT FLOW.

    // timer(2000)
    //   .pipe(first(), takeUntilDestroyed(this.destroyRef))
    //   .subscribe(() => {
    //     if (failedResponseMock.result) {
    //       failedResponseMock.result = {
    //         ...failedResponseMock.result,
    //         status: PAYMENT_STATUS.failed,
    //       };
    //     }

    //     paymentMockError$.next(failedResponseMock);
    //   });

    // timer(2000)
    //   .pipe(first(), takeUntilDestroyed(this.destroyRef))
    //   .subscribe(() => {
    //     paymentMockSucces$.next(successResponseMock);
    //   });

    // REAL PAYMENT

    // MOCKS
    // const successResponseMock = integratedPaymentMockSuccess;
    // const failedResponseMock = integratedPaymentMockError;
    // const successPayment = this.payment;

    // successPayment.updateStatus(PAYMENT_STATUS.completed);
    // successPayment.updateAdditionalData(paymentAdditionalDataMock);

    // successResponseMock.result = successPayment;
    // failedResponseMock.result = null;

    // const paymentMockSucces$ =
    //   new ReplaySubject<IApiPaymentTransactionResponse>(1);
    // const paymentMockError$ = new ReplaySubject<IApiPaymentTransactionResponse>(
    //   1
    // );

    const paymentEvents$ = race(
      paymentResponse$,
      paymentResponseError$,
      // paymentMockSucces$,
      // paymentMockError$
    );

    let loaderRef: SplashartLoaderComponent | null = null;

    if (isTerminalPayment) {
      loaderRef = this.globalSplashArtLoaderService.open({
        loadingTexts: ['fv_modal.payment_loader.msg'],
      });

      this.trackPayment(`Loader initiated`);
    }

    return paymentEvents$.pipe(
      first(),
      switchMap((response) => {
        this.logger.debug(
          `[PAYMENTS 💶💶] [INTEGRATED] [PAYMENT RESPONSE]`,
          JSON.stringify(response)
        );

        if (loaderRef) {
          this.globalSplashArtLoaderService.close();
        }

        this.trackPayment(`Integrated payment response`, response);

        if (response.success) {
          this.trackPayment(`Integrated payment response SUCCESS`);

          this.logger.debug(`[PAYMENTS 💶💶] [INTEGRATED] [Success PAYMENT]`);

          const paymentFromBack: Payment = PaymentMapper.toModel(
            PaymentMapper.toStorageFromApi(response.result as IApiPayment)
          );

          this.trackPayment(`Payment success from back`, {
            customData: {
              paymentId: paymentFromBack._id,
            },
          });

          return this.handleSuccessIntegratedPayment(paymentFromBack).pipe(
            tap(() => {
              if (this.currentTicketEvent) {
                this.trackPayment(
                  'Has TicketEvent',
                  {
                    customData: {
                      ticketEventId: this.currentTicketEvent._id,
                    },
                  }
                );

                this.paymentsCommonService.updateTicketEvent(
                  this.currentTicketEvent,
                  this.payment
                );
              }
            }),
            map((ticket) => {
              const paymentResponse: IApiPaymentTransactionResponse = {
                ...response,
                ticket,
                payment: paymentFromBack,
              };

              return paymentResponse;
            })
          );
        } else {
          this.logger.debug(`[PAYMENTS 💶💶] [INTEGRATED] [Failed PAYMENT]`);

          this.trackPayment('Integrated payment response FAILED', response);

          return this.handleErrorIntegratedPayment();
        }
      }),
      catchError(() => {
        if (loaderRef) {
          this.globalSplashArtLoaderService.close();

          this.trackPayment(`Loader closed: FAILED`);
        }

        this.logger.debug(`[PAYMENTS 💶💶] [INTEGRATED] [Failed PAYMENT]`);

        return this.handleErrorIntegratedPayment();
      }),
      finalize(() => {
        if (loaderRef) {
          this.globalSplashArtLoaderService.close();
        }

        this.capacitorService.removeListener('responseAdyenPayment');
        this.capacitorService.removeListener('responseAdyenPaymentError');

        this.trackPayment('Removed payment listeners');
      })
    );
  }

  /**
   * Handles successful integrated payment responses.
   * Updates the account status, processes ticket information, and persists
   * the changes to the database.
   *
   * @param paymentFromBack - The payment object received from the backend
   * @returns Observable with the ticket or null if no ticket was generated
   */
  private handleSuccessIntegratedPayment(
    paymentFromBack: Payment
  ): Observable<Ticket | null> {
    this.currentAccount?.updateClosed(this.paymentDetails.isAccountClosed);

    if (
      this.paymentDetails.entriesToPay?.length &&
      this.paymentDetails.paymentSummary.paymentType === PAYMENT_TYPE.byProduct
    ) {
      const entriesRenderedAfterPayment = this.paymentDetails.entriesRenderedAfterPayment;

      this.currentAccount?.updateEntries(entriesRenderedAfterPayment);
      this.eventBus.publish(new EntriesChangedEvent(entriesRenderedAfterPayment, 'updated'));
    }

    if (this.paymentDetails.isAccountClosed) {
      this.currentAccount?.updateClosedAccountTime(new Date());
      this.currentAccount?.setCloseTime(Date.now());
    }

    this.currentAccount?.updateSerie(this.paymentDetails.ticketSerie ?? '');

    this.currentAccount?.updatePayment(paymentFromBack);

    const insertTicket$ = this.paymentsCommonService.handleTicketInsertion(
      this.ticket,
      false
    );

    return insertTicket$.pipe(
      first(),
      switchMap((ticket) => {
        return combineLatest({
          selectedAccount: this.accountStoreService.saveSelectedAccount(
            this.currentAccount as Account
          ),
          allAccounts: this.accountStoreService.getAllAccounts(),
        }).pipe(map(() => ticket));
      })
    );
  }

  /**
   * Handles errors that occur during integrated payment processing.
   * Updates the payment status to failed, persists the changes, and shows
   * a confirmation dialog to the user with retry options.
   *
   * @returns Observable with the payment transaction response
   */
  private handleErrorIntegratedPayment(): Observable<IApiPaymentTransactionResponse> {
    this.payment.updateStatus(PAYMENT_STATUS.failed);
    this.currentAccount?.updatePayment(this.payment);

    this.trackPayment('Updated payment to failed status', {
      customData: {
        paymentId: this.payment._id,
      },
    });

    return combineLatest({
      selectedAccount: this.accountStoreService.saveSelectedAccount(
        this.currentAccount as Account
      ),
      allAccounts: this.accountStoreService.saveAccounts([
        this.currentAccount as Account,
      ]),
    }).pipe(
      first(),
      switchMap(() => timer(500)),
      switchMap(() => this.openIntegratedPaymentErrorConfirm().pipe(first())),
      switchMap((flow) => {
        if (flow === PAYMENT_FLOW.integrated) {
          this.trackPayment('Retry with integrated payment');

          return this.processPayment(this.paymentDetails);
        }

        if (flow === PAYMENT_FLOW.manual) {
          this.trackPayment('Retry with manual payment');

          return this.processManualPaymentUseCase
            .execute(this.paymentDetails)
            .pipe(
              map((result) => ({
                success: result.success,
                payment: result.payment,
                ticket: result.ticket,
                key: PAYMENT_FLOW.manual,
                message: null,
              }))
            );
        }

        const cancelResponse: IApiPaymentTransactionResponse = {
          key: PAYMENT_FLOW.integrated,
          success: false,
          message: 'Payment cancelled by user',
          payment: null,
          ticket: null,
        };

        this.trackPayment('Canceled retry');

        return of(cancelResponse);
      }),
      catchError((error) => {
        this.logger.error(
          new Error(
            '[PaymentService][handleErrorIntegratedPayment] Error processing payment'
          ),
          error
        );

        return of({
          key: PAYMENT_FLOW.integrated,
          success: false,
          message: 'Integrated payment processing failed',
          code: -1,
          payment: null,
          ticket: null,
        });
      })
    );
  }

  /**
   * Opens a confirmation dialog when an integrated payment fails.
   * Provides options to retry with integrated payment, use manual payment,
   * or cancel the payment process.
   *
   * @returns Observable with the selected payment flow or null if canceled
   */
  private openIntegratedPaymentErrorConfirm(): Observable<PAYMENT_FLOW | null> {
    return new Observable((observer) => {
      this.trackPayment('Failed integrated payment modal opened');

      const buttons = [
        {
          text: this.translateService.instant('global.retry'),
          color: BUTTON_COLOR.error,
          size: BUTTON_SIZE.full,
          type: BUTTON_TYPE.filled,
          preventAutoClose: false,
          handler: (): void => {
            this.globalConfirmService.close();
            observer.next(PAYMENT_FLOW.integrated);
            observer.complete();
          },
        },
      ];

      buttons.push({
        text: this.translateService.instant('global.cancel'),
        color: BUTTON_COLOR.transparent,
        size: BUTTON_SIZE.full,
        type: BUTTON_TYPE.filled,
        preventAutoClose: true,
        handler: (): void => {
          this.globalConfirmService.close();
          observer.next(null);
          observer.complete();
        },
      });

      this.globalConfirmService.open({
        noClose: false,
        message: this.translateService.instant(
          'fv_confirm.payment.error_card_payment'
        ),
        submessage: this.translateService.instant(
          'fv_confirm.payment.retry_payment_option_title'
        ),
        hideCloseIcon: true,
        onClosed: () => {
          observer.next(null);
          observer.complete();
        },
        buttons,
      });
    });
  }

  /**
   * Generates the payload required for integrated payment processing.
   * Includes account, payment, ticket and authorization details.
   *
   * @returns The integrated payment payload
   */
  private generateIntegratedPayload(): IApiPaymentIntegratedPayload {
    return {
      account: this.currentAccount
        ? AccountApiMapper.toApi(this.currentAccount)
        : undefined,
      entries: this.paymentDetails.entriesToPay.map((entry) =>
        EntryMapper.toApi(entry)
      ),
      payment: PaymentMapper.toApi(this.payment),
      closed: this.paymentDetails.isAccountClosed,
      ticket: this.ticket ? TicketMapper.toJSON(this.ticket) : undefined,
      audits: [this.currentAudit as IAudit],
      authorId: this.currentWorker?.id ?? NOT_FOUND_LABEL,
      negocio_id: this.organization?._id ?? NOT_FOUND_LABEL,
      token: this.token ?? NOT_FOUND_LABEL,
      provider: this.paymentDetails.provider ?? PAYMENT_PROVIDER.adyen,
      poiid: this.terminalService.currentTerminal?.poiid ?? null,
    };
  }

  /**
   * Handles failed payment scenarios by updating or creating a new payment.
   * Determines if a payment has changed and takes appropriate action.
   *
   * @param failedPayment - The failed payment to process
   * @param paymentInfo - Information about the payment context
   */
  private onPaymentFailed(
    failedPayment: Payment,
    paymentInfo: IPaymentInfo
  ): void {
    const isPaymentChanged: boolean = hasPaymentChanged(
      failedPayment,
      paymentInfo
    );

    const pendingPayments =
      this.currentAccount?.getPaymentsByStatus(PAYMENT_STATUS.pending) || [];

    if (failedPayment && !isPaymentChanged) {
      this.payment = Object.assign(this.payment, failedPayment);

      for (const pendingPayment of pendingPayments) {
        this.currentAccount?.deletePayment(pendingPayment._id);
      }

      this.trackPayment(
        'Failed payment not changed, deleting pending payments'
      );
    } else {
      if (isPaymentChanged && failedPayment) {
        this.currentAccount?.deletePayment(failedPayment._id);

        this.trackPayment('Failed payment changed. Delete payment', {
          customData: {
            paymentId: failedPayment._id,
          },
        });
      }

      for (const pendingPayment of pendingPayments) {
        this.currentAccount?.deletePayment(pendingPayment._id);
      }

      this.trackPayment('Deleted pending payments');

      this.payment = this.createPayment();

      this.payment.updateOperation(formatPaymentOperation(this.payment));

      const hasComplementEntries = this.currentAccount?.entries?.some(
        (entry) => entry?.consumption?.type === CONSUMPTION_TYPE.complement
      );

      if (hasComplementEntries) {
        this.payment.updateAdditionalData({
          ticket_event_id: this.currentTicketEvent?._id,
        });
      }

      this.currentAccount?.addPayment(this.payment);

      this.trackPayment('New payment created on failed payment', {
        customData: {
          paymentId: this.payment._id,
        },
      });
    }
  }

  /**
   * Creates a new payment object with the necessary details.
   *
   * @returns A new payment object
   */
  private createPayment(): Payment {
    return PaymentMapper.toModel({
      _id: uuid.v4(),
      amount: this.paymentDetails.paymentSummary.totalAmount,
      cash_drawer_count_id: '',
      cash_drawer_id: this.currentDrawer?.id ?? '',
      created_at: Date.now(),
      created_by: this.currentWorker?.id ?? '',
      divisa: this.organization?.currency ?? DEFAULT_CURRENCY,
      negocio_id: this.organization?._id ?? '',
      payment_method: this.paymentDetails.paymentSummary.paymentMethod,
      payment_type: this.paymentDetails.paymentSummary.paymentType,
      paymentResume: this.paymentDetails.paymentSummary,
      serie: this.paymentDetails.ticketSerie ?? '',
      status: PAYMENT_STATUS.pending,
      entries: this.paymentDetails.entriesToPay.map((entry) =>
        EntryMapper.toStorage(entry)
      ),
      operation: undefined,
      renderedEntries: this.paymentDetails.entriesRenderedAfterPayment.map(
        (entry) => EntryMapper.toStorage(entry)
      ),
      isProductPayment:
        this.paymentDetails.paymentSummary.paymentType ===
        PAYMENT_TYPE.byProduct,
    });
  }

  /**
   * Retrieves payment-related data from IndexedDB that's necessary for payment processing.
   * Includes account, drawer, waiter, organization, and audit information.
   *
   * @returns Observable with the payment data from IndexedDB
   */
  private getPaymentNecessaryData(): Observable<IPaymentIndexedDBData> {
    return this.paymentsCommonService
      .getNecessaryPaymentDataFromIndexedDB()
      .pipe(
        first(),
        tap(
          ({
            currentAccount,
            currentDrawer,
            currentWorker,
            organization,
            currentAudit,
            currentTicketEvent,
          }) => {
            this.device = this.deviceService.device;
            this.currentAccount = currentAccount;
            this.currentDrawer = currentDrawer;
            this.organization = organization;
            this.currentTicketEvent = currentTicketEvent;
            this.currentAudit = currentAudit;
            this.currentWorker = currentWorker;

            if (!this.device) {
              this.logger.error(new Error('Error fetching current device'));
            }

            if (!this.currentAccount) {
              this.logger.error(new Error('Error fetching current account'));
            }

            if (!organization) {
              this.logger.error(new Error('Error fetching organization'));
            }
          }
        )
      );
  }

  /**
   * Tracks the type of integrated payment being used.
   *
   * @param isTerminalPayment - Whether the payment is through a physical terminal or Tap2Pay
   */
  private trackIntegratedType(isTerminalPayment: boolean): void {
    if (isTerminalPayment) {
      this.trackPayment('Integrated payment with Terminal');
    } else {
      this.trackPayment('Integrated payment with Tap2Pay');
    }
  }
}
